import React, { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import ReactGoogleAutocomplete from "react-google-autocomplete";
import Constants from "../../config/Constants";

/**
 * LocationSearch Component
 * 
 * A reusable component for location search using Google Places Autocomplete.
 * Falls back to a regular TextField if Google Maps API key is not available.
 * 
 * @param {string} value - Current location value
 * @param {function} onChange - Callback when location changes (receives { address, lat, lng })
 * @param {function} onBlur - Callback when input loses focus
 * @param {string} placeholder - Placeholder text
 * @param {string} name - Input name for form handling
 * @param {boolean} error - Error state for Material-UI TextField
 * @param {string} helperText - Helper text for Material-UI TextField
 * @param {object} className - CSS class name
 * @param {object} style - Custom styles
 * @param {object} ...otherProps - Other props to pass to TextField
 */
const LocationSearch = ({
  value = "",
  onChange,
  onBlur,
  placeholder = "Enter location",
  name = "location",
  error = false,
  helperText = "",
  className = "",
  style = {},
  ...otherProps
}) => {
  const [address, setAddress] = useState(value || "");
  const mapAccess = Constants.MAP_API_KEY;

  // Sync local state with prop value when it changes externally (e.g., clear filters)
  useEffect(() => {
    setAddress(value || "");
  }, [value]);

  const handlePlaceSelected = (place) => {
    try {
      // Extract latitude and longitude
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();
      
      // Get formatted address string
      const addressString = place?.formatted_address || place?.name || address;
      
      // Update local state
      setAddress(addressString);
      
      // Call onChange callback with all location data
      if (onChange) {
        // Call with structured data (two parameters)
        onChange(
          {
            target: {
              name: name,
              value: addressString,
            },
          },
          {
            address: addressString,
            lat: lat,
            lng: lng,
          }
        );
      }
    } catch (error) {
      console.error("Error handling place selection:", error);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value || "";
    setAddress(newValue);
    // Update the text but don't call onChange with location data yet
    // Only update location filter when a place is selected
  };

  const handleInputBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  // Debug: Log API key availability (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (!mapAccess) {
        console.warn("⚠️ Google Maps API key not found. LocationSearch will fallback to regular input.");
        console.log("Check .env file and ensure REACT_APP_MAP_API_KEY is set.");
      } else {
        console.log("✅ Google Maps API key loaded:", mapAccess.substring(0, 15) + "...");
      }
    }
  }, [mapAccess]);

  // Use Google Autocomplete if API key is available
  if (mapAccess) {
    return (
      <div style={{ position: "relative", width: "100%", zIndex: 1300 }}>
        <ReactGoogleAutocomplete
          apiKey={mapAccess}
          onPlaceSelected={handlePlaceSelected}
          placeholder={placeholder}
          defaultValue={address || value || ""}
          onChange={handleTextChange}
          onBlur={handleInputBlur}
          className={className}
          style={{
            width: "100%",
            padding: "14.5px 14px",
            fontSize: "14px",
            fontFamily: "Roboto, sans-serif",
            border: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            outline: "none",
            color: style.color || "#0a0a0a",
            backgroundColor: style.backgroundColor || "transparent",
            ...style,
          }}
          // No options = works globally (all countries, all location types)
          // To restrict to specific countries, add:
          // options={{ componentRestrictions: { country: "au" } }}
        />
        {helperText && (
          <div
            style={{
              fontSize: "0.75rem",
              marginTop: "3px",
              marginLeft: "14px",
              color: error ? "#d32f2f" : "rgba(0, 0, 0, 0.6)",
            }}
          >
            {helperText}
          </div>
        )}
      </div>
    );
  }

  // Fallback to regular TextField if API key not available
  return (
    <TextField
      name={name}
      value={address}
      onChange={handleTextChange}
      onBlur={onBlur}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      className={className}
      style={style}
      fullWidth
      {...otherProps}
    />
  );
};

export default LocationSearch;
